import { Permission } from '@Constants/Permission.g';
import { DOCUMENTS_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import { useFeatureFlags } from '@Contexts/featureFlags';
import { useTransparentRef } from '@Hooks/useTransparentRef';
import { AiProvider, type ApiDynamicExportDefinition } from '@Model';
import { yaml as yamlLang } from '@codemirror/lang-yaml';
import {
    Button,
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    isNullOrEmpty,
    Select,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useModalState,
    useTranslate,
} from '@synqo/frontend-component-library';
import CodeMirror from '@uiw/react-codemirror';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { GenerateWithAiModal } from './GenerateWithAiModal';
import { useGenerateExportDefinition } from './useGenerateExportDefinition';
import { exportTypeOptions, getUniqueExportIdentifier } from './exportTypeOptions';

export function FrameEditDynamicExportDefinition(props: IFrameEditShellProps<ApiDynamicExportDefinition>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiDynamicExportDefinition>
            apiResourcePath={`${DOCUMENTS_API}/export_definitions`}
            renderer={FrameEditDynamicExportDefinitionBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.DocumentsDynamicExportRunCreate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.DocumentsDynamicExportRunCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.DocumentsDynamicExportRunDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditDynamicExportDefinitionBody(props: IFrameBodyProps<ApiDynamicExportDefinition>) {
    const i18n = useTranslate('');

    const { requestUpdateState } = props;
    const { isOpen: isAiModalOpen, close: closeAiModal, open: openAiModal } = useModalState();
    const { generate, isGenerating } = useGenerateExportDefinition();
    const { isFeatureEnabled } = useFeatureFlags();

    // The generate endpoint is the one thing here behind the AI flag - the editor itself is not.
    // Without this check the button would offer a call the server answers with 404.
    const canGenerateWithAi = isFeatureEnabled('AI_FEATURES');

    const isNotViewMode = props.mode !== EditFrameMode.View;

    const { category, key } = props.data;
    const exportIdentifier = getUniqueExportIdentifier(category, key);

    const binder = useGuiBinder<ApiDynamicExportDefinition>({
        ...props,
        canEdit: isNotViewMode,
    });

    const { onChange, onConfirm, locked } = binder.bindToProp('definition');

    const yamlExtensions = useMemo(() => [yamlLang()], []);

    const yaml = useTransparentRef(props.data.definition, !isNotViewMode);

    const handleYamlChange = useCallback(
        (value: string) => {
            const change = onChange(value, true);
            if (!change) {
                return;
            }

            yaml.current = value;

            return true;
        },
        [onChange, yaml],
    );

    const handleYamlConfirm = useCallback(() => {
        if (!onConfirm) {
            return;
        }

        return onConfirm(yaml.current, true);
    }, [onConfirm, yaml]);

    const handleGenerate = useCallback(
        async (prompt: string, provider: AiProvider) => {
            const generatedYaml = await generate(provider, prompt, category, key);

            if (isNullOrEmpty(generatedYaml)) {
                return false;
            }

            closeAiModal();

            const changed = handleYamlChange(generatedYaml);
            if (!changed) {
                return false;
            }

            if (onConfirm) {
                onConfirm(generatedYaml, true);
            }
        },
        [category, key, generate, closeAiModal, handleYamlChange, onConfirm],
    );

    const handleParseExportType = useCallback(
        (identifier: string) => {
            if (!identifier.includes(':')) {
                return false;
            }

            const [category, key] = identifier.split(':', 2);

            requestUpdateState((latest, saveItem) =>
                saveItem({
                    ...latest,
                    category,
                    key,
                }),
            );

            return true;
        },
        [requestUpdateState],
    );

    const headerProps = useMemo(
        () => ({
            label: getFrameLabel('Export-Beschreibung', props.mode),
            resourceLabel: 'Export-Beschreibung',
            ...props.events,
            actionItems: canGenerateWithAi
                ? [
                      <Button key="generate" onClick={openAiModal} locked={!isNotViewMode || isGenerating}>
                          Mit KI generieren
                      </Button>,
                  ]
                : [],
        }),
        [props.mode, props.events, openAiModal, isNotViewMode, isGenerating, canGenerateWithAi],
    );

    return (
        <EditFrameShell
            headerProps={headerProps}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="main" border="withoutBorder">
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <Select
                            {...binder.bindToAutoConfirmed<string>('key')}
                            items={exportTypeOptions}
                            label={i18n('Art')}
                            value={exportIdentifier}
                            onChange={handleParseExportType}
                        />
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                    </GridColumns>
                </Grid>
                <CodeMirror
                    key={props.mode}
                    value={yaml.current}
                    height={'500px'}
                    extensions={yamlExtensions}
                    onChange={handleYamlChange}
                    onBlur={handleYamlConfirm}
                    editable={!locked}
                />
            </Card>
            <GenerateWithAiModal
                isOpen={canGenerateWithAi && isAiModalOpen}
                close={closeAiModal}
                description="Beschreiben Sie, welchen Export Sie erstellen möchten und welche Daten enthalten sein sollen. Die KI wird basierend auf Ihrer Beschreibung ein Export-Script generieren."
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
            />
        </EditFrameShell>
    );
}
